import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { newsletterTerms } from "@/lib/skin";
import { StatusBadge } from "@/components/StatusBadge";
import { TestBadge } from "@/components/TestBadge";
import { Card } from "@/components/ui/card";
import {
  Inbox, CalendarClock, AlertCircle, ShieldQuestion, RefreshCw, Factory, Send,
  Newspaper, AlertTriangle, CheckCircle2, ClipboardList, Loader2, Palette, PlusCircle, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

const QUEUE_DEFS = [
  { key: "awaiting_leadership", label: "Awaiting Leadership", icon: ShieldQuestion, accent: true, leadership: true },
  { key: "approved_for_production", label: "Approved for Production", icon: CheckCircle2 },
  { key: "approved_with_edits", label: "Approved w/ Edits", icon: RefreshCw },
  { key: "production_review", label: "Production Review", icon: ClipboardList },
  { key: "additional_info", label: "Info Required", icon: AlertCircle },
  { key: "in_production", label: "In Production", icon: Factory },
  { key: "ready_to_release", label: "Ready to Release", icon: Send },
  { key: "sunday_news_queue", label: "Sunday News Queue", icon: Newspaper, feature: "publication_builder" },
  { key: "due_this_week", label: "Due This Week", icon: CalendarClock },
  { key: "overdue", label: "Overdue", icon: AlertTriangle },
  { key: "denied", label: "Denied", icon: Inbox },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
];

export default function AdminDashboard() {
  const { user, features } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const isLeadership = user?.role === "president" || user?.role === "vice_president";
  const [active, setActive] = useState(isLeadership ? "awaiting_leadership" : "approved_for_production");
  const queueDefs = QUEUE_DEFS.filter((q) => !q.feature || features[q.feature])
    .map((q) => (q.key === "sunday_news_queue" ? { ...q, label: newsletterTerms().queue } : q));

  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const queue = data.queues[active] || [];

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">
            {isLeadership ? "Leadership Review" : "Communications Dashboard"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isLeadership ? "Approve, approve with edits, or deny requests awaiting leadership authorization." : "Requests cleared by leadership, ready for production review and delivery."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" className="rounded-full" onClick={() => navigate("/submit/comms-request")} data-testid="admin-submit-request-btn">
            <FileText className="mr-1.5 h-4 w-4" /> Submit a Request
          </Button>
          {features.publication_builder && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate("/submit/sunday-news")} data-testid="admin-submit-sunday-news-btn">
              <PlusCircle className="mr-1.5 h-4 w-4" /> {newsletterTerms().submit}
            </Button>
          )}
          <div className="rounded-xl border border-border bg-card px-4 py-2 text-center">
            <p className="font-heading text-xl font-bold text-primary">{data.counts.total}</p>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total</p>
          </div>
          <div className="rounded-xl border border-border bg-card px-4 py-2 text-center">
            <p className="font-heading text-xl font-bold text-accent">{data.counts.awaiting_leadership}</p>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Awaiting</p>
          </div>
        </div>
      </div>

      {/* Queue cards */}
      <div data-testid="admin-queue-grid" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {queueDefs.map((q, i) => (
          <motion.button key={q.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
            onClick={() => setActive(q.key)} data-testid={`queue-card-${q.key}`}
            className={`flex flex-col items-start rounded-xl border p-4 text-left transition-transform hover:-translate-y-1 ${active === q.key ? "border-primary bg-primary/5 shadow-md" : q.accent ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}>
            <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${active === q.key ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground/70"}`}>
              <q.icon className="h-4 w-4" />
            </div>
            <p className="mt-3 font-heading text-2xl font-bold">{data.counts[q.key] ?? 0}</p>
            <p className="text-xs text-muted-foreground">{q.label}</p>
          </motion.button>
        ))}
      </div>

      {/* Active queue list */}
      <Card className="p-0">
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="font-heading text-lg font-semibold">{queueDefs.find((q) => q.key === active)?.label}</h2>
          <span className="text-sm text-muted-foreground">{queue.length} item(s)</span>
        </div>
        <div className="divide-y divide-border" data-testid="queue-list">
          {queue.length === 0 && <p className="p-10 text-center text-sm text-muted-foreground">This queue is empty. Nice work!</p>}
          {queue.map((s) => (
            <button key={s.id} onClick={() => navigate(`/submissions/${s.id}`)} data-testid={`queue-item-${s.id}`}
              className="flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-secondary/50">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${s.submission_type === "sunday_news" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
                {s.submission_type === "sunday_news" ? <Newspaper className="h-5 w-5" /> : <Palette className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{s.data?.title || s.data?.project_purpose || "Untitled"}</p>
                <p className="text-xs text-muted-foreground">
                  {s.committee || "—"} · {s.creator_name || "Member"}
                  {s.assigned_name ? ` · ➜ ${s.assigned_name}` : ""}
                  {s.data?.final_deadline ? ` · due ${s.data.final_deadline}` : s.data?.requested_publication_date ? ` · pub ${s.data.requested_publication_date}` : ""}
                </p>
              </div>
              {s.is_test && <TestBadge />}
              {s.priority === "rush" && <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive">Rush</span>}
              <StatusBadge status={s.status} />
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
